<template>
  <AppError v-if="errorMessage" :error-message />
</template>

<script setup lang="ts">
import { useAuth } from '@/composables/use-auth'
import AppError from './AppError.vue'
import type { AuthState } from '@/composables/use-auth'
import type { ErrorResponse } from 'oidc-client-ts'

const { handleLoginCallback } = useAuth()

const errorMessage = ref()

const RETRY_STORAGE_KEY = 'auth-callback-retry-at'
const RETRY_COOLDOWN_MS = 60 * 1000

function shouldRetry() {
  const lastRetryAt = Number(sessionStorage.getItem(RETRY_STORAGE_KEY) ?? 0)
  return Date.now() - lastRetryAt > RETRY_COOLDOWN_MS
}

function redirect(state: unknown) {
  const redirectTo = (state as AuthState | undefined)?.redirectTo ?? '/'
  const isRelativePath =
    redirectTo.startsWith('/') && !redirectTo.startsWith('//')
  window.location.replace(isRelativePath ? redirectTo : '/')
}

onMounted(async () => {
  try {
    const user = await handleLoginCallback()
    sessionStorage.removeItem(RETRY_STORAGE_KEY)
    redirect(user?.state)
  } catch (error) {
    const err = error as ErrorResponse
    console.error(err)

    // the main app either restores the existing session or starts a fresh login
    if (shouldRetry()) {
      sessionStorage.setItem(RETRY_STORAGE_KEY, String(Date.now()))
      redirect(err?.state)
      return
    }

    errorMessage.value =
      err?.message ?? 'Unknown error during auth initialization'
    // replace the url so the user can try to reload
    window.history.replaceState({}, '', '/')
  }
})
</script>
